/**
 * Cart Add Interceptor
 *
 * Intercepts all cart/add.js API requests (via fetch or XMLHttpRequest)
 * and dispatches CART_ITEM_ADDED events for analytics tracking.
 *
 * This approach bypasses DOM event listeners which can be intercepted
 * by third-party apps (SavedBy, UpCart, etc.) and ensures all cart
 * additions are tracked regardless of source.
 *
 * Supports three Shopify cart add request formats:
 * 1. Single item (JSON): { id, quantity, ... }
 * 2. Multiple items (JSON): { items: [{ id, quantity, ... }] }
 * 3. Form data: FormData with id and quantity fields
 *
 * All responses are normalized to items array for consistent event dispatching.
 */

const CONFIG = {
	DEBUG_ENABLED: false,
	DECIMAL_RADIX: 10,
	CENTS_TO_DOLLARS: 100,
};

const HTTP_STATUS = {
	OK: 200,
	MULTIPLE_CHOICES: 300,
};

const CART_ADD_ENDPOINTS = ['/cart/add.js', '/cart/add'];

window.UFT = window.UFT || {};
window.UFT.CartAddInterceptor = (function () {
	class CartAddInterceptor {
		constructor() {
			this.interceptFetch();
			this.interceptXHR();
		}

		interceptFetch() {
			const originalFetch = window.fetch;
			const self = this;

			window.fetch = function (url, options) {
				const urlString = typeof url === 'string' ? url : url?.url;

				if (!self.isCartAddRequest(urlString)) {
					return originalFetch.apply(this, arguments);
				}

				self.log('Intercepted cart/add fetch request', { url: urlString });
				const requestBody = options?.body;

				return originalFetch.apply(this, arguments).then((response) => {
					return self.handleFetchResponse(response, requestBody);
				});
			};
		}

		interceptXHR() {
			const originalOpen = XMLHttpRequest.prototype.open;
			const originalSend = XMLHttpRequest.prototype.send;
			const self = this;

			XMLHttpRequest.prototype.open = function (method, url) {
				this._url = url;
				return originalOpen.apply(this, arguments);
			};

			XMLHttpRequest.prototype.send = function (body) {
				if (!self.isCartAddRequest(this._url)) {
					return originalSend.apply(this, arguments);
				}

				self.log('Intercepted cart/add XHR request', { url: this._url });
				this._requestBody = body;
				this.addEventListener('load', self.createXHRLoadListener(self, body));

				return originalSend.apply(this, arguments);
			};
		}

		handleFetchResponse(response, requestBody) {
			if (!response.ok) {
				return response;
			}

			return this.parseFetchResponseJSON(response, requestBody);
		}

		parseFetchResponseJSON(response, requestBody) {
			return response
				.clone()
				.json()
				.then((data) => {
					this.handleCartAddResponse(data, requestBody);
					return response;
				})
				.catch((error) => {
					console.warn(error);
					return response;
				});
		}

		createXHRLoadListener(self, requestBody) {
			return function () {
				if (!self.isSuccessStatus(this.status)) {
					return;
				}

				try {
					const data = JSON.parse(this.responseText);
					self.handleCartAddResponse(data, this._requestBody);
				} catch (error) {
					console.warn(error);
				}
			};
		}

		handleCartAddResponse(response, requestBody) {
			if (!window.UFT?.EventManager) {
				return;
			}

			try {
				this.log('Received cart/add response', response);
				const cartItems = this.normalizeCartResponse(response);
				const items = cartItems.map((item) => this.transformCartItemToEventItem(item, requestBody));

				this.log('Dispatching cart:item-added event', { items });
				this.dispatchCartItemAddedEvent(items);
			} catch (error) {
				console.warn(error);
			}
		}

		normalizeCartResponse(response) {
			return response.items || [response];
		}

		transformCartItemToEventItem(item, requestBody) {
			const baseEventItem = this.createBaseEventItem(item, requestBody);
			return this.enrichEventItem(baseEventItem, item);
		}

		createBaseEventItem(item, requestBody) {
			const quantity = this.extractQtyFromRequest(requestBody, item.variant_id);

			return {
				productId: item.product_id,
				variantId: item.variant_id,
				quantity: quantity,
				productName: item.product_title,
				variantTitle: item.variant_title,
				price: item.price,
				sku: item.sku,
				productUrl: item.url,
			};
		}

		enrichEventItem(eventItem, item) {
			this.addOptionalProductType(eventItem, item.product_type);
			this.addDiscountIfApplicable(eventItem, item);
			this.addSubscriptionFrequencyIfApplicable(eventItem, item);
			this.addItemProductTypeIfApplicable(eventItem, item);
			return eventItem;
		}

		dispatchCartItemAddedEvent(items) {
			const eventData = { items };
			window.UFT.EventManager.dispatch(window.UFT.EventManager.eventStrings.CART_ITEM_ADDED, eventData);
		}

		log(message, data) {
			if (!CONFIG.DEBUG_ENABLED) {
				return;
			}

			if (data) {
				console.log(`[CartAddInterceptor] ${message}`, data);
			} else {
				console.log(`[CartAddInterceptor] ${message}`);
			}
		}

		addOptionalProductType(eventItem, productType) {
			if (productType != null && productType !== '') {
				eventItem.productType = productType;
			}
		}

		addDiscountIfApplicable(eventItem, item) {
			if (item.total_discount != null && item.total_discount > 0) {
				const discountInDollars = item.total_discount / CONFIG.CENTS_TO_DOLLARS;
				eventItem.discount = parseFloat(discountInDollars.toFixed(2));
			} else {
				this.addDiscountFromSellingPlanIfApplicable(eventItem, item);
			}
		}

		addDiscountFromSellingPlanIfApplicable(eventItem, item) {
			const sellingPlanAllocation = item.selling_plan_allocation;
			if (sellingPlanAllocation?.compare_at_price != null && sellingPlanAllocation.compare_at_price > 0) {
				const subscriptionDiscount = sellingPlanAllocation.compare_at_price - sellingPlanAllocation.price;

				if (subscriptionDiscount > 0) {
					const discountInDollars = subscriptionDiscount / CONFIG.CENTS_TO_DOLLARS;
					eventItem.discount = parseFloat(discountInDollars.toFixed(2));
				}
			}
		}

		addSubscriptionFrequencyIfApplicable(eventItem, item) {
			const sellingPlanName = item.selling_plan_allocation?.selling_plan?.name;

			if (sellingPlanName) {
				eventItem.itemSubscriptionFrequency = sellingPlanName;
			}
		}

		addItemProductTypeIfApplicable(eventItem, item) {
			if (item.product_type === 'Beluga-Bundle') {
				eventItem.itemProductType = 'Beluga Bundle';
				return;
			}

			if (item.product_type === 'Classic Box') {
				eventItem.itemProductType = 'Skio Classic Bundle';
				return;
			}

			if (item.properties?._dynamicBoxId != null) {
				eventItem.itemProductType = 'Skio Dynamic Bundle';
				return;
			}

			eventItem.itemProductType = 'Standalone Product';
		}

		extractQtyFromRequest(requestBody, variantId) {
			try {
				if (typeof requestBody === 'string') {
					return this.parseQtyFromRequestBodyJSON(requestBody, variantId);
				}

				if (requestBody instanceof FormData) {
					return this.extractQtyFromFormData(requestBody);
				}
			} catch (error) {
				console.warn(error);
			}

			return null;
		}

		parseQtyFromRequestBodyJSON(requestBody, variantId) {
			const parsed = JSON.parse(requestBody);

			if (parsed.items && Array.isArray(parsed.items)) {
				return this.extractQtyFromItemsArray(parsed.items, variantId);
			}

			const singleItemQuantity = this.extractQtyFromSingleItem(parsed, variantId);
			if (singleItemQuantity !== null) {
				return singleItemQuantity;
			}

			console.warn('Failed to parse add to cart quantity.');
			return null;
		}

		extractQtyFromFormData(formData) {
			const quantity = formData.get('quantity');
			return this.parseQuantityValue(quantity);
		}

		extractQtyFromItemsArray(items, variantId) {
			const item = items.find((item) => String(item.id) === String(variantId));

			if (!item) {
				return null;
			}

			return this.parseQuantityValue(item.quantity);
		}

		extractQtyFromSingleItem(parsed, variantId) {
			if (String(parsed.id) !== String(variantId)) {
				return null;
			}

			return this.parseQuantityValue(parsed.quantity);
		}

		parseQuantityValue(value) {
			const parsed = parseInt(value, CONFIG.DECIMAL_RADIX);
			return isNaN(parsed) ? null : parsed;
		}

		isCartAddRequest(url) {
			if (!url || typeof url !== 'string') {
				return false;
			}

			return CART_ADD_ENDPOINTS.some((endpoint) => url.includes(endpoint));
		}

		isSuccessStatus(status) {
			return status >= HTTP_STATUS.OK && status < HTTP_STATUS.MULTIPLE_CHOICES;
		}
	}

	return new CartAddInterceptor();
})();
